import React, { useState, useMemo } from "react";

// icons
import { FiSearch } from "react-icons/fi";
import { MdAddCircleOutline, MdVisibility, MdArrowUpward, MdArrowDownward, MdGroups } from "react-icons/md";
import Card from "../../../../components/card";
import api from "../../../../services/axios";
import SkeletonTable from "../../../../components/skeleton/table";
import ModalAddTeam from "./ModalAddTeam";
import ModalViewDetailTeam from "./ModalViewDetailTeam";

interface TableColumn {
  key: string;
  header: string;
}

interface TableRow {
  [key: string]: string | number;
}

interface ResponsiveTableProps {
  columns: TableColumn[];
  data: TableRow[];
}

const selectCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

const TeamManagementTable: React.FC<ResponsiveTableProps> = ({
  columns,
  data,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortedColumn, setSortedColumn] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [openAddTeamModal, setOpenAddTeamModal] = useState<boolean>(false);
  const [openTeamDetailModal, setTeamDetailModal] = useState<boolean>(false);
  const [userDetail, setUserDetail] = useState<object | null>(null);
  const [userData, setUserData] = useState<TableRow[]>(data);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedClientId, setSelectedClientId] = useState<number | "">("");

  const clients = useMemo(() => {
    const map = new Map<number, string>();
    userData.forEach((row) => {
      if (row.clientId == null) return;
      map.set(Number(row.clientId), String(row.nmCorporate ?? ""));
    });
    return Array.from(map, ([clientId, nmCorporate]) => ({ clientId, nmCorporate }))
      .sort((a, b) => a.nmCorporate.localeCompare(b.nmCorporate));
  }, [userData]);

  const handleSort = (columnKey: string) => {
    setSortedColumn(columnKey);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const clientFiltered = selectedClientId === ""
    ? userData
    : userData.filter((row) => Number(row.clientId) === selectedClientId);

  const filteredData = clientFiltered.filter((row) =>
    Object.values(row).some((value) =>
      value
        ? value.toString().toLowerCase().includes(searchTerm?.toLowerCase())
        : ""
    )
  );

  const sortedData = sortedColumn
    ? [...filteredData].sort((a, b) =>
        sortOrder === "asc"
          ? a[sortedColumn] > b[sortedColumn]
            ? 1
            : -1
          : a[sortedColumn] < b[sortedColumn]
          ? 1
          : -1
      )
    : filteredData;

  const handleRowClick = (row: TableRow) => {
    setUserDetail(row);
    setTeamDetailModal(true);
  };

  const handleOpenModal = () => {
    setOpenAddTeamModal(true);
  };

  const getDetails = async () => {
    setLoading(true);
    api
      .get("/EmployeeClientProject/list")
      .then((response) => {
        setUserData(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("There was an error!", error);
        setLoading(true);
      });
  };

  return (
    <div>
      {loading ? (
        <SkeletonTable active={loading} />
      ) : (
        <div className="animate-fade-up">
          <Card extra="p-5">
            {/* Toolbar header: add action */}
            <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={handleOpenModal}
                className="dv-sheen flex items-center justify-center gap-1.5 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98]"
              >
                <MdAddCircleOutline className="h-4 w-4" aria-hidden="true" />
                Adicionar
              </button>
            </div>

            {/* Toolbar: filter + search */}
            <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="team-client-filter"
                  className="text-sm font-semibold text-gray-700 dark:text-gray-300"
                >
                  Cliente
                </label>
                <select
                  id="team-client-filter"
                  value={selectedClientId}
                  onChange={(e) =>
                    setSelectedClientId(e.target.value === "" ? "" : Number(e.target.value))
                  }
                  className={selectCls}
                >
                  <option value="">Todos os clientes</option>
                  {clients.map((c) => (
                    <option key={c.clientId} value={c.clientId}>
                      {c.nmCorporate}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="team-search"
                  className="text-sm font-semibold text-gray-700 dark:text-gray-300"
                >
                  Buscar
                </label>
                <div className="relative">
                  <FiSearch
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    aria-hidden="true"
                  />
                  <input
                    id="team-search"
                    type="text"
                    placeholder="Buscar por nome, e-mail, projeto…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`${selectCls} pl-9`}
                  />
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-[0_8px_30px_rgba(112,144,176,0.12)] dark:border-white/10 dark:bg-navy-800">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-navy-900">
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        scope="col"
                        className="cursor-pointer select-none px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                        onClick={() => handleSort(column.key)}
                      >
                        <span className="inline-flex items-center gap-1">
                          {column.header}
                          {sortedColumn === column.key &&
                            (sortOrder === "asc" ? (
                              <MdArrowUpward className="h-3.5 w-3.5 text-dv-blue-500" aria-hidden="true" />
                            ) : (
                              <MdArrowDownward className="h-3.5 w-3.5 text-dv-blue-500" aria-hidden="true" />
                            ))}
                        </span>
                      </th>
                    ))}
                    <th
                      scope="col"
                      className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                    >
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="border-b border-gray-100 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700"
                    >
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className={`px-4 py-3 ${
                            column.key === "id" ? "tabular-nums" : ""
                          } ${
                            column.key === "nmEmployee"
                              ? "font-semibold text-navy-700 dark:text-white"
                              : "text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {row[column.key]}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          aria-label="Ver detalhes do colaborador"
                          title="Ver detalhes"
                          onClick={() => handleRowClick(row)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-all hover:bg-gray-50 hover:text-dv-blue-600 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
                        >
                          <MdVisibility className="h-4 w-4" aria-hidden="true" />
                          Detalhes
                        </button>
                      </td>
                    </tr>
                  ))}
                  {sortedData.length === 0 && (
                    <tr>
                      <td colSpan={columns.length + 1} className="px-4 py-12">
                        <div className="flex flex-col items-center justify-center gap-2 text-center">
                          <span
                            className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 dark:bg-navy-900 dark:text-gray-500"
                            aria-hidden="true"
                          >
                            <MdGroups className="h-6 w-6" />
                          </span>
                          <p className="text-sm font-semibold text-navy-700 dark:text-white">
                            Nenhum colaborador encontrado
                          </p>
                          <p className="text-xs text-gray-700 dark:text-gray-300">
                            Ajuste a busca ou o filtro de cliente.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      <ModalAddTeam
        openAddTeamModal={openAddTeamModal}
        setOpenAddTeamModal={setOpenAddTeamModal}
        updateState={() => getDetails()}
        details={userDetail}
      ></ModalAddTeam>
      <ModalViewDetailTeam
        openTeamDetailModal={openTeamDetailModal}
        setOpenTeamDetailModal={setTeamDetailModal}
        details={userDetail}
      ></ModalViewDetailTeam>
    </div>
  );
};

export default TeamManagementTable;
